import { useEffect, useRef, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider, useT } from "./i18n";
import { api } from "./lib/api";
import { Layout } from "./components/Layout";
import AuthPage from "./pages/AuthPage";
import LandingPage from "./pages/LandingPage";
import OnboardingPage from "./pages/OnboardingPage";
import DashboardPage from "./pages/DashboardPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ExportPage from "./pages/ExportPage";
import TeamPage from "./pages/TeamPage";
import ReportsPage from "./pages/ReportsPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import JoinInvitePage from "./pages/JoinInvitePage";
import OverviewPage from "./pages/OverviewPage";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    api.post("/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", "/");
        navigate("/", { replace: true });
      })
      .catch(() => {
        window.history.replaceState(null, "", "/");
        navigate("/", { replace: true });
      });
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <p className="font-mono text-sm text-slate-400">Signing you in…</p>
    </div>
  );
};

const Shell = () => {
  const { user, loading } = useAuth();
  const { t } = useT();
  const location = useLocation();
  const [workspace, setWorkspace] = useState(null);
  const [wsLoading, setWsLoading] = useState(true);

  const loadWorkspace = useCallback(async () => {
    try {
      const { data } = await api.get("/workspaces/me");
      setWorkspace(data.workspace);
    } catch {
      setWorkspace(null);
    } finally {
      setWsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.workspace_id) loadWorkspace();
    else setWsLoading(false);
  }, [user, loadWorkspace]);

  // Auto-accept pending company invitation after sign-in
  useEffect(() => {
    const tok = localStorage.getItem("pending_invite");
    if (user && tok) {
      localStorage.removeItem("pending_invite");
      api.post(`/invitations/token/${tok}/accept`).then(() => window.location.reload()).catch(() => {});
    }
  }, [user]);

  if (loading || (user && user.workspace_id && wsLoading))
    return <div className="min-h-screen flex items-center justify-center bg-white"><p className="font-mono text-sm text-slate-400">{t("loading")}</p></div>;

  if (!user) return location.pathname === "/login" ? <AuthPage /> : <LandingPage />;
  if (!user.workspace_id || !workspace) return <OnboardingPage onDone={loadWorkspace} />;

  const isManager = ["admin", "manager"].includes(user.role);
  const isTeam = workspace.type === "team";

  return (
    <Layout workspace={workspace}>
      <Routes>
        <Route path="/" element={<DashboardPage workspace={workspace} />} />
        <Route path="/reports" element={<ReportsPage workspace={workspace} />} />
        {isTeam && isManager && <Route path="/approvals" element={<ApprovalsPage workspace={workspace} />} />}
        <Route path="/export" element={<ExportPage />} />
        {isTeam && isManager && <Route path="/team" element={<TeamPage />} />}
        {user?.memberships?.some((m) => m.role === "admin") && <Route path="/overview" element={<OverviewPage />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (location.pathname.startsWith("/reset-password")) return <ResetPasswordPage />;
  if (location.pathname.startsWith("/join")) return <JoinInvitePage />;
  return <Shell />;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
